import React, { memo, useEffect, useState } from 'react';
import styles from './PWAAppScreens.module.scss';
import NotificationPermission from '../NotificationPermission';
import utilStorage, { NotificationConsent } from '@src/shared/services/util-storage';
import MessageBanner from '../MessageBanner';
import { PWAInstallTutorial } from '../PWAInstallTutorial';
import { askBrowserNotificationPermission } from '@src/shared/utils';
import {
  usePermissionConsentActions,
  usePermissionConsentState,
} from '@src/shared/context/permission-consent';
import useProgressiveWebApp from '@src/shared/hooks/use-pwa';
import PWAInstallMessage from '../PWAInstallMessage';
import { useTranslation } from 'react-i18next';

export const PWAAppScreens = () => {
  //const [permissionConsent, setPermissionConsent] = useState<NotificationConsent | any>(utilStorage.getNotificationPermissionConsent());
  const PWATutorialPostponeStatus = utilStorage.getPWATutorialPostpone();
  const { consent, grantType, hideTemporary } = usePermissionConsentState();
  const { setPermissionConsent, setNotificationGrantType, setNotificationScreenAsTemp } =
    usePermissionConsentActions();
  const { isAppInstalled } = useProgressiveWebApp();
  const [showPWATutorialMessage, setShowPWATutorialMessage] = useState<null | Boolean>(null);
  const [isOtherBottomSheetsVisible, setIsOtherBottomSheetsVisible] = useState(false);
  const pwaTutorialCount = utilStorage.getPWATutorialCounter();
  const { i18n } = useTranslation();

  const isNotificationPermissionScreenPathPermitted = () => {
    const permittedPaths = ['/deliveries', '/couriers'];
    return permittedPaths.includes(window.location.pathname);
  };
  const handleNotificationPermissionCTA = (data: NotificationConsent) => {
    switch (data) {
      case NotificationConsent.CONSENT_FULL:
        setPermissionConsent(data);
        askBrowserNotificationPermission().then((permission) => {
          return setNotificationGrantType(permission);
        });
        // Request Notification Screen
        break;
      case NotificationConsent.SHOW_LATER:
        setPermissionConsent(data);
        //Request Notification Message Stable
        break;
      case NotificationConsent.ON_REFRESH:
        setNotificationScreenAsTemp();
        break;
    }
  };

  useEffect(() => {
    const domObserver = new MutationObserver(() => {
      const bottomSheetElements = document.getElementById('delivery-dashboard-bottom-sheet');
      if (bottomSheetElements) {
        setIsOtherBottomSheetsVisible(true);
      } else {
        setIsOtherBottomSheetsVisible(false);
      }
    });

    domObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => {
      domObserver.disconnect();
    };
  }, []);

  const renderPWAInstallTutorialFlow = () => {
    if (pwaTutorialCount <= 3) {
      return showPWATutorialMessage ? (
        <PWAInstallMessage
          onClose={() => {
            setShowPWATutorialMessage(false);
          }}
          onCTA={() => {
            setShowPWATutorialMessage(false);
          }}
        />
      ) : (
        <PWAInstallTutorial
          messageScreenStatus={showPWATutorialMessage}
          onPostPone={() => {
            setShowPWATutorialMessage(true);
          }}
        />
      );
    } else {
      return null;
    }
  };

  const isMessageEnabled = () => {
    return !!(
      (!isAppInstalled && pwaTutorialCount <= 3 && showPWATutorialMessage) ||
      (isAppInstalled &&
        consent === NotificationConsent.SHOW_LATER &&
        isAppInstalled &&
        grantType === 'default')
    );
  };

  useEffect(() => {
    if (consent) {
      utilStorage.removeStoredItem('notification-permission-consent');
      utilStorage.setNotificationPermissionConsent(consent);
    }
  }, [consent]);

  useEffect(() => {
    if (PWATutorialPostponeStatus) {
      if (PWATutorialPostponeStatus === 'NOT_SET') {
        setShowPWATutorialMessage(true);
      } else {
        setShowPWATutorialMessage(false);
      }
    }
  }, []);

  useEffect(() => {
    if (grantType) {
      utilStorage.removeStoredItem('notification-grant-type');
      utilStorage.setNotificationGrantType(grantType);
    }
  }, [grantType]);

  useEffect(() => {
    const container = document.getElementById('pwaAppScreensContainer');
    const child = container?.firstElementChild as HTMLElement;
    window.requestAnimationFrame(() => {
      if (isMessageEnabled() && container && child) {
        container.style.height = `${child.offsetHeight + 10}px`;

        container.style.display = 'block';
      } else if (!isMessageEnabled() && !showPWATutorialMessage && container) {
        container.style.height = '0px';
        container.style.marginBottom = `0px`;
      }
    });
  }, [i18n.language, isMessageEnabled()]);

  return (
    (!isOtherBottomSheetsVisible && (
      <div
        id="pwaAppScreensContainer"
        className={`${styles.pwaAppScreens} ${isMessageEnabled() ? styles.messageEnabled : ''}`}
      >
        {isAppInstalled && grantType === 'default' ? (
          <>
            {consent === NotificationConsent.SHOW_LATER && <MessageBanner />}
            {!consent && !hideTemporary && isNotificationPermissionScreenPathPermitted() && (
              <NotificationPermission key={'perm'} onConsent={handleNotificationPermissionCTA} />
            )}
          </>
        ) : (
          !isAppInstalled && renderPWAInstallTutorialFlow()
        )}
      </div>
    )) ||
    null
  );
};

export default PWAAppScreens;
